import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { JOB_CATEGORIES, JobCategory } from '@/lib/types/job';
import { format, startOfMonth, startOfWeek, differenceInDays, subDays } from 'date-fns';

// ===========================================
// Types
// ===========================================

export interface DiversityKPIs {
  totalSpendCents: number;
  diverseSpendCents: number;
  diverseSpendPercentage: number;
  diverseJobCount: number;
  totalJobCount: number;
  diverseVendorCount: number;
  totalVendorCount: number;
  diverseVendorPercentage: number;
  diverseWinRate: number;
  diverseBidsSubmitted: number;
  diverseJobsAwarded: number;
  // Previous period comparisons
  prevDiverseSpendCents: number;
  prevDiversePercentage: number;
  prevDiverseVendorCount: number;
  prevWinRate: number;
}

export interface CertificationSpend {
  certificationTypeId: string;
  certificationName: string;
  categoryName: string;
  spendCents: number;
  jobCount: number;
  vendorCount: number;
  percentageOfDiverseSpend: number;
  percentageOfTotalSpend: number;
}

export interface DiversityTrendPoint {
  period: string;
  periodStart: Date;
  diverseSpendCents: number;
  totalSpendCents: number;
  diversePercentage: number;
  diverseJobCount: number;
  totalJobCount: number;
}

export interface OpportunityFunnel {
  jobsWithDiverseInvited: number;
  jobsWithDiverseBid: number;
  jobsAwardedToDiverse: number;
  totalJobs: number;
  invitedPercentage: number;
  bidPercentage: number;
  awardedPercentage: number;
}

export interface CategoryDiversitySpend {
  category: JobCategory;
  categoryLabel: string;
  diverseSpendCents: number;
  nonDiverseSpendCents: number;
  totalSpendCents: number;
  diversePercentage: number;
  diverseJobCount: number;
  totalJobCount: number;
}

export interface GeographySpend {
  state: string;
  diverseSpendCents: number;
  diverseVendorCount: number;
  jobCount: number;
  percentageOfDiverseSpend: number;
}

export interface TopDiverseVendor {
  vendorId: string;
  vendorName: string;
  certifications: string[];
  jobsAwarded: number;
  totalSpendCents: number;
  percentageOfDiverseSpend: number;
  bidsSubmitted: number;
  winRate: number;
  avgJobValueCents: number;
}

export interface CertificationType {
  id: string;
  name: string;
  categoryName: string;
}

export interface DiversityExportData {
  kpis: DiversityKPIs;
  certifications: CertificationSpend[];
  trends: DiversityTrendPoint[];
  categories: CategoryDiversitySpend[];
  geography: GeographySpend[];
  vendors: TopDiverseVendor[];
  jobs: Array<{
    jobId: string;
    publicId: string;
    title: string;
    category: string;
    vendorName: string;
    certifications: string[];
    amountCents: number;
    awardedDate: string;
  }>;
}

// ===========================================
// Helper constants
// ===========================================

const DIVERSITY_CATEGORY_NAMES = ['Diversity Certifications', 'Small Business Certifications'];

function getCategoryLabel(category: string): string {
  const found = JOB_CATEGORIES.find(c => c.value === category);
  return found?.label || 'Other';
}

// ===========================================
// Hook: useDiversityCertificationTypes
// Fetches available diversity certification types for filtering
// ===========================================

export function useDiversityCertificationTypes() {
  return useQuery({
    queryKey: ['diversity-certification-types'],
    queryFn: async (): Promise<CertificationType[]> => {
      const { data, error } = await supabase
        .from('certification_types')
        .select(`
          id,
          name,
          category:certification_categories!inner(name)
        `)
        .eq('is_active', true);

      if (error) throw error;

      // Filter to only diversity-related categories
      return (data || [])
        .filter((ct: any) => DIVERSITY_CATEGORY_NAMES.includes(ct.category?.name))
        .map((ct: any) => ({
          id: ct.id,
          name: ct.name,
          categoryName: ct.category?.name || '',
        }));
    },
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
}

// ===========================================
// Hook: useDiversityKPIs
// Main KPI metrics with period comparison
// ===========================================

export function useDiversityKPIs(
  startDate: Date,
  endDate: Date,
  organizationId: string | undefined,
  selectedCategories: string[] = [],
  selectedCertifications: string[] = []
) {
  return useQuery({
    queryKey: ['diversity-kpis', startDate.toISOString(), endDate.toISOString(), organizationId, selectedCategories, selectedCertifications],
    queryFn: async (): Promise<DiversityKPIs> => {
      if (!organizationId) {
        return {
          totalSpendCents: 0,
          diverseSpendCents: 0,
          diverseSpendPercentage: 0,
          diverseJobCount: 0,
          totalJobCount: 0,
          diverseVendorCount: 0,
          totalVendorCount: 0,
          diverseVendorPercentage: 0,
          diverseWinRate: 0,
          diverseBidsSubmitted: 0,
          diverseJobsAwarded: 0,
          prevDiverseSpendCents: 0,
          prevDiversePercentage: 0,
          prevDiverseVendorCount: 0,
          prevWinRate: 0,
        };
      }

      // Calculate previous period (same duration, before start date)
      const periodDays = differenceInDays(endDate, startDate);
      const prevEndDate = subDays(startDate, 1);
      const prevStartDate = subDays(prevEndDate, periodDays);

      // Fetch current period data
      const currentData = await fetchPeriodData(organizationId, startDate, endDate, selectedCategories, selectedCertifications);
      
      // Fetch previous period data
      const prevData = await fetchPeriodData(organizationId, prevStartDate, prevEndDate, selectedCategories, selectedCertifications);

      // Fetch bidding data for win rate
      const biddingData = await fetchBiddingData(organizationId, startDate, endDate, selectedCertifications);
      const prevBiddingData = await fetchBiddingData(organizationId, prevStartDate, prevEndDate, selectedCertifications);

      const diversePercentage = currentData.totalSpendCents > 0 
        ? (currentData.diverseSpendCents / currentData.totalSpendCents) * 100 
        : 0;
      
      const prevDiversePercentage = prevData.totalSpendCents > 0 
        ? (prevData.diverseSpendCents / prevData.totalSpendCents) * 100 
        : 0;

      const diverseVendorPercentage = currentData.totalVendorCount > 0
        ? (currentData.diverseVendorCount / currentData.totalVendorCount) * 100
        : 0;

      const winRate = biddingData.diverseBidsSubmitted > 0
        ? (biddingData.diverseJobsAwarded / biddingData.diverseBidsSubmitted) * 100
        : 0;

      const prevWinRate = prevBiddingData.diverseBidsSubmitted > 0
        ? (prevBiddingData.diverseJobsAwarded / prevBiddingData.diverseBidsSubmitted) * 100
        : 0;

      return {
        totalSpendCents: currentData.totalSpendCents,
        diverseSpendCents: currentData.diverseSpendCents,
        diverseSpendPercentage: diversePercentage,
        diverseJobCount: currentData.diverseJobCount,
        totalJobCount: currentData.totalJobCount,
        diverseVendorCount: currentData.diverseVendorCount,
        totalVendorCount: currentData.totalVendorCount,
        diverseVendorPercentage,
        diverseWinRate: winRate,
        diverseBidsSubmitted: biddingData.diverseBidsSubmitted,
        diverseJobsAwarded: biddingData.diverseJobsAwarded,
        prevDiverseSpendCents: prevData.diverseSpendCents,
        prevDiversePercentage,
        prevDiverseVendorCount: prevData.diverseVendorCount,
        prevWinRate,
      };
    },
    enabled: !!organizationId,
  });
}

async function fetchPeriodData(
  organizationId: string,
  startDate: Date,
  endDate: Date,
  selectedCategories: string[],
  selectedCertifications: string[]
) {
  // Fetch all jobs with POs in the period
  let query = supabase
    .from('jobs')
    .select(`
      id,
      category,
      purchase_order:purchase_orders!left(
        po_amount_cents,
        vendor_organization_id
      )
    `)
    .eq('organization_id', organizationId)
    .gte('updated_at', startDate.toISOString())
    .lte('updated_at', endDate.toISOString())
    .in('status', ['accepted', 'closed', 'in_progress', 'completed_pending_review', 'awarded']);

  if (selectedCategories.length > 0) {
    query = query.in('category', selectedCategories);
  }

  const { data: jobs, error } = await query;
  if (error) throw error;

  // Collect all vendor org IDs
  const vendorOrgIds = new Set<string>();
  for (const job of jobs || []) {
    const po = Array.isArray(job.purchase_order) ? job.purchase_order[0] : job.purchase_order;
    if (po?.vendor_organization_id) {
      vendorOrgIds.add(po.vendor_organization_id);
    }
  }

  // Fetch diversity certifications for these vendors
  const diverseVendorMap = new Map<string, string[]>(); // vendor_org_id -> cert type IDs
  
  if (vendorOrgIds.size > 0) {
    const { data: vendorProfiles } = await supabase
      .from('vendor_profiles_enhanced')
      .select('id, organization_id')
      .in('organization_id', Array.from(vendorOrgIds));

    const profileIds = (vendorProfiles || []).map(v => v.id);
    const profileToOrgMap = new Map<string, string>();
    for (const vp of vendorProfiles || []) {
      profileToOrgMap.set(vp.id, vp.organization_id);
    }

    if (profileIds.length > 0) {
      let certQuery = supabase
        .from('vendor_certifications')
        .select(`
          vendor_profile_id,
          certification_type_id,
          certification_type:certification_types!inner(
            id,
            name,
            category:certification_categories!inner(name)
          )
        `)
        .in('vendor_profile_id', profileIds);

      const { data: certs } = await certQuery;

      for (const cert of certs || []) {
        const catName = (cert.certification_type as any)?.category?.name;
        if (!DIVERSITY_CATEGORY_NAMES.includes(catName)) continue;
        
        // Apply certification filter if specified
        if (selectedCertifications.length > 0 && !selectedCertifications.includes(cert.certification_type_id)) {
          continue;
        }

        const orgId = profileToOrgMap.get(cert.vendor_profile_id);
        if (orgId) {
          const existing = diverseVendorMap.get(orgId) || [];
          if (!existing.includes(cert.certification_type_id)) {
            existing.push(cert.certification_type_id);
          }
          diverseVendorMap.set(orgId, existing);
        }
      }
    }
  }

  // Calculate metrics
  let totalSpendCents = 0;
  let diverseSpendCents = 0;
  let totalJobCount = 0;
  let diverseJobCount = 0;
  const allVendors = new Set<string>();
  const diverseVendors = new Set<string>();

  for (const job of jobs || []) {
    const po = Array.isArray(job.purchase_order) ? job.purchase_order[0] : job.purchase_order;
    if (!po?.po_amount_cents) continue;

    totalSpendCents += po.po_amount_cents;
    totalJobCount++;
    
    if (po.vendor_organization_id) {
      allVendors.add(po.vendor_organization_id);
      
      if (diverseVendorMap.has(po.vendor_organization_id)) {
        diverseSpendCents += po.po_amount_cents;
        diverseJobCount++;
        diverseVendors.add(po.vendor_organization_id);
      }
    }
  }

  return {
    totalSpendCents,
    diverseSpendCents,
    totalJobCount,
    diverseJobCount,
    totalVendorCount: allVendors.size,
    diverseVendorCount: diverseVendors.size,
  };
}

async function fetchBiddingData(
  organizationId: string,
  startDate: Date,
  endDate: Date,
  selectedCertifications: string[]
) {
  // Get RFQs for jobs in the organization within the date range
  const { data: rfqs } = await supabase
    .from('rfqs')
    .select(`
      id,
      job_id,
      job:jobs!inner(
        id,
        organization_id,
        status,
        updated_at
      )
    `)
    .eq('job.organization_id', organizationId)
    .gte('job.updated_at', startDate.toISOString())
    .lte('job.updated_at', endDate.toISOString());

  if (!rfqs || rfqs.length === 0) {
    return { diverseBidsSubmitted: 0, diverseJobsAwarded: 0 };
  }

  const rfqIds = rfqs.map(r => r.id);

  // Get bids for these RFQs
  const { data: bids } = await supabase
    .from('bids')
    .select(`
      id,
      rfq_id,
      vendor_user_id,
      status
    `)
    .in('rfq_id', rfqIds);

  if (!bids || bids.length === 0) {
    return { diverseBidsSubmitted: 0, diverseJobsAwarded: 0 };
  }

  // Get vendor profiles for bid submitters
  const vendorUserIds = [...new Set(bids.map(b => b.vendor_user_id))];
  
  const { data: vendorProfiles } = await supabase
    .from('vendor_profiles_enhanced')
    .select('id, user_id, organization_id')
    .in('user_id', vendorUserIds);

  const userToProfileMap = new Map<string, string>();
  const userToOrgMap = new Map<string, string>();
  for (const vp of vendorProfiles || []) {
    userToProfileMap.set(vp.user_id, vp.id);
    userToOrgMap.set(vp.user_id, vp.organization_id);
  }

  // Get certifications for these vendor profiles
  const profileIds = (vendorProfiles || []).map(v => v.id);
  const diverseVendorUserIds = new Set<string>();

  if (profileIds.length > 0) {
    const { data: certs } = await supabase
      .from('vendor_certifications')
      .select(`
        vendor_profile_id,
        certification_type_id,
        certification_type:certification_types!inner(
          category:certification_categories!inner(name)
        )
      `)
      .in('vendor_profile_id', profileIds);

    const diverseProfileIds = new Set<string>();
    for (const cert of certs || []) {
      const catName = (cert.certification_type as any)?.category?.name;
      if (!DIVERSITY_CATEGORY_NAMES.includes(catName)) continue;
      
      if (selectedCertifications.length > 0 && !selectedCertifications.includes(cert.certification_type_id)) {
        continue;
      }
      
      diverseProfileIds.add(cert.vendor_profile_id);
    }

    for (const vp of vendorProfiles || []) {
      if (diverseProfileIds.has(vp.id)) {
        diverseVendorUserIds.add(vp.user_id);
      }
    }
  }

  // Count diverse bids and awards
  let diverseBidsSubmitted = 0;
  let diverseJobsAwarded = 0;

  const rfqIdToJobId = new Map<string, string>();
  for (const rfq of rfqs) {
    rfqIdToJobId.set(rfq.id, rfq.job_id);
  }

  // Get awards to determine winning bids
  const jobIds = rfqs.map(r => r.job_id);
  const { data: awards } = await supabase
    .from('awards')
    .select('job_id, winning_bid_id')
    .in('job_id', jobIds);

  const awardedBidIds = new Set((awards || []).map(a => a.winning_bid_id).filter(Boolean));

  for (const bid of bids) {
    if (diverseVendorUserIds.has(bid.vendor_user_id)) {
      diverseBidsSubmitted++;
      if (awardedBidIds.has(bid.id)) {
        diverseJobsAwarded++;
      }
    }
  }

  return { diverseBidsSubmitted, diverseJobsAwarded };
}

// ===========================================
// Hook: useSpendByCertification
// Breakdown by certification type
// ===========================================

export function useSpendByCertification(
  startDate: Date,
  endDate: Date,
  organizationId: string | undefined,
  selectedCategories: string[] = [],
  selectedCertifications: string[] = []
) {
  return useQuery({
    queryKey: ['spend-by-certification', startDate.toISOString(), endDate.toISOString(), organizationId, selectedCategories, selectedCertifications],
    queryFn: async (): Promise<CertificationSpend[]> => {
      if (!organizationId) return [];

      // Fetch jobs with POs
      let query = supabase
        .from('jobs')
        .select(`
          id,
          category,
          purchase_order:purchase_orders!left(
            po_amount_cents,
            vendor_organization_id
          )
        `)
        .eq('organization_id', organizationId)
        .gte('updated_at', startDate.toISOString())
        .lte('updated_at', endDate.toISOString())
        .in('status', ['accepted', 'closed', 'in_progress', 'completed_pending_review', 'awarded']);

      if (selectedCategories.length > 0) {
        query = query.in('category', selectedCategories);
      }

      const { data: jobs, error } = await query;
      if (error) throw error;

      // Build vendor org -> job spend map
      const vendorJobSpend = new Map<string, { spendCents: number; jobIds: Set<string> }>();
      let totalSpendCents = 0;

      for (const job of jobs || []) {
        const po = Array.isArray(job.purchase_order) ? job.purchase_order[0] : job.purchase_order;
        if (!po?.vendor_organization_id || !po?.po_amount_cents) continue;

        totalSpendCents += po.po_amount_cents;
        const existing = vendorJobSpend.get(po.vendor_organization_id) || { spendCents: 0, jobIds: new Set() };
        existing.spendCents += po.po_amount_cents;
        existing.jobIds.add(job.id);
        vendorJobSpend.set(po.vendor_organization_id, existing);
      }

      // Fetch vendor profiles
      const vendorOrgIds = Array.from(vendorJobSpend.keys());
      if (vendorOrgIds.length === 0) return [];

      const { data: vendorProfiles } = await supabase
        .from('vendor_profiles_enhanced')
        .select('id, organization_id')
        .in('organization_id', vendorOrgIds);

      const profileToOrgMap = new Map<string, string>();
      const orgToProfileMap = new Map<string, string>();
      for (const vp of vendorProfiles || []) {
        profileToOrgMap.set(vp.id, vp.organization_id);
        orgToProfileMap.set(vp.organization_id, vp.id);
      }

      const profileIds = (vendorProfiles || []).map(v => v.id);

      // Fetch certifications
      let certQuery = supabase
        .from('vendor_certifications')
        .select(`
          vendor_profile_id,
          certification_type_id,
          certification_type:certification_types!inner(
            id,
            name,
            category:certification_categories!inner(name)
          )
        `)
        .in('vendor_profile_id', profileIds);

      const { data: certs } = await certQuery;

      // Build certification -> spend mapping
      // A vendor with multiple certs has their spend counted in each
      const certSpendMap = new Map<string, {
        name: string;
        categoryName: string;
        spendCents: number;
        jobIds: Set<string>;
        vendorIds: Set<string>;
      }>();

      let totalDiverseSpend = 0;
      const diverseVendorOrgIds = new Set<string>();

      for (const cert of certs || []) {
        const catName = (cert.certification_type as any)?.category?.name;
        if (!DIVERSITY_CATEGORY_NAMES.includes(catName)) continue;

        const certTypeId = cert.certification_type_id;
        const certName = (cert.certification_type as any)?.name || 'Unknown';
        
        if (selectedCertifications.length > 0 && !selectedCertifications.includes(certTypeId)) {
          continue;
        }

        const orgId = profileToOrgMap.get(cert.vendor_profile_id);
        if (!orgId || !vendorJobSpend.has(orgId)) continue;

        const vendorData = vendorJobSpend.get(orgId)!;
        
        // Track diverse vendor for total calculation (avoid double counting)
        if (!diverseVendorOrgIds.has(orgId)) {
          diverseVendorOrgIds.add(orgId);
          totalDiverseSpend += vendorData.spendCents;
        }

        const existing = certSpendMap.get(certTypeId) || {
          name: certName,
          categoryName: catName,
          spendCents: 0,
          jobIds: new Set(),
          vendorIds: new Set(),
        };

        // Only add if we haven't counted this vendor for this cert
        if (!existing.vendorIds.has(orgId)) {
          existing.spendCents += vendorData.spendCents;
          vendorData.jobIds.forEach(jid => existing.jobIds.add(jid));
          existing.vendorIds.add(orgId);
        }
        certSpendMap.set(certTypeId, existing);
      }

      // Convert to array
      const result: CertificationSpend[] = Array.from(certSpendMap.entries())
        .map(([certId, data]) => ({
          certificationTypeId: certId,
          certificationName: data.name,
          categoryName: data.categoryName,
          spendCents: data.spendCents,
          jobCount: data.jobIds.size,
          vendorCount: data.vendorIds.size,
          percentageOfDiverseSpend: totalDiverseSpend > 0 ? (data.spendCents / totalDiverseSpend) * 100 : 0,
          percentageOfTotalSpend: totalSpendCents > 0 ? (data.spendCents / totalSpendCents) * 100 : 0,
        }))
        .sort((a, b) => b.spendCents - a.spendCents);

      return result;
    },
    enabled: !!organizationId,
  });
}

// ===========================================
// Hook: useDiversityTrend
// Time-series diversity spend data
// ===========================================

export function useDiversityTrend(
  startDate: Date,
  endDate: Date,
  organizationId: string | undefined,
  selectedCategories: string[] = [],
  selectedCertifications: string[] = []
) {
  return useQuery({
    queryKey: ['diversity-trend', startDate.toISOString(), endDate.toISOString(), organizationId, selectedCategories, selectedCertifications],
    queryFn: async (): Promise<DiversityTrendPoint[]> => {
      if (!organizationId) return [];

      // Fetch jobs with POs
      let query = supabase
        .from('jobs')
        .select(`
          id,
          category,
          updated_at,
          purchase_order:purchase_orders!left(
            po_amount_cents,
            vendor_organization_id
          )
        `)
        .eq('organization_id', organizationId)
        .gte('updated_at', startDate.toISOString())
        .lte('updated_at', endDate.toISOString())
        .in('status', ['accepted', 'closed', 'in_progress', 'completed_pending_review', 'awarded']);

      if (selectedCategories.length > 0) {
        query = query.in('category', selectedCategories);
      }

      const { data: jobs, error } = await query;
      if (error) throw error;

      // Get all vendor org IDs
      const vendorOrgIds = new Set<string>();
      for (const job of jobs || []) {
        const po = Array.isArray(job.purchase_order) ? job.purchase_order[0] : job.purchase_order;
        if (po?.vendor_organization_id) {
          vendorOrgIds.add(po.vendor_organization_id);
        }
      }

      // Fetch diverse vendors
      const diverseVendorOrgIds = await fetchDiverseVendorOrgIds(Array.from(vendorOrgIds), selectedCertifications);

      // Determine bucket size
      const daysDiff = differenceInDays(endDate, startDate);
      const useWeekly = daysDiff < 90;

      // Aggregate by period
      const periodMap = new Map<string, {
        periodStart: Date;
        diverseSpendCents: number;
        totalSpendCents: number;
        diverseJobCount: number;
        totalJobCount: number;
      }>();

      for (const job of jobs || []) {
        const po = Array.isArray(job.purchase_order) ? job.purchase_order[0] : job.purchase_order;
        if (!po?.po_amount_cents) continue;

        const date = new Date(job.updated_at);
        const periodStart = useWeekly ? startOfWeek(date, { weekStartsOn: 1 }) : startOfMonth(date);
        const periodKey = periodStart.toISOString();

        const existing = periodMap.get(periodKey) || {
          periodStart,
          diverseSpendCents: 0,
          totalSpendCents: 0,
          diverseJobCount: 0,
          totalJobCount: 0,
        };

        existing.totalSpendCents += po.po_amount_cents;
        existing.totalJobCount++;

        if (po.vendor_organization_id && diverseVendorOrgIds.has(po.vendor_organization_id)) {
          existing.diverseSpendCents += po.po_amount_cents;
          existing.diverseJobCount++;
        }

        periodMap.set(periodKey, existing);
      }

      // Convert to array
      return Array.from(periodMap.values())
        .map(data => ({
          period: useWeekly
            ? `Week of ${format(data.periodStart, 'MMM d')}`
            : format(data.periodStart, 'MMM yyyy'),
          periodStart: data.periodStart,
          diverseSpendCents: data.diverseSpendCents,
          totalSpendCents: data.totalSpendCents,
          diversePercentage: data.totalSpendCents > 0 
            ? (data.diverseSpendCents / data.totalSpendCents) * 100 
            : 0,
          diverseJobCount: data.diverseJobCount,
          totalJobCount: data.totalJobCount,
        }))
        .sort((a, b) => a.periodStart.getTime() - b.periodStart.getTime());
    },
    enabled: !!organizationId,
  });
}

async function fetchDiverseVendorOrgIds(vendorOrgIds: string[], selectedCertifications: string[]): Promise<Set<string>> {
  if (vendorOrgIds.length === 0) return new Set();

  const { data: vendorProfiles } = await supabase
    .from('vendor_profiles_enhanced')
    .select('id, organization_id')
    .in('organization_id', vendorOrgIds);

  const profileToOrgMap = new Map<string, string>();
  for (const vp of vendorProfiles || []) {
    profileToOrgMap.set(vp.id, vp.organization_id);
  }

  const profileIds = (vendorProfiles || []).map(v => v.id);
  if (profileIds.length === 0) return new Set();

  const { data: certs } = await supabase
    .from('vendor_certifications')
    .select(`
      vendor_profile_id,
      certification_type_id,
      certification_type:certification_types!inner(
        category:certification_categories!inner(name)
      )
    `)
    .in('vendor_profile_id', profileIds);

  const diverseOrgIds = new Set<string>();
  for (const cert of certs || []) {
    const catName = (cert.certification_type as any)?.category?.name;
    if (!DIVERSITY_CATEGORY_NAMES.includes(catName)) continue;
    
    if (selectedCertifications.length > 0 && !selectedCertifications.includes(cert.certification_type_id)) {
      continue;
    }

    const orgId = profileToOrgMap.get(cert.vendor_profile_id);
    if (orgId) diverseOrgIds.add(orgId);
  }

  return diverseOrgIds;
}

// ===========================================
// Hook: useOpportunityFunnel
// Participation rates through procurement process
// ===========================================

export function useOpportunityFunnel(
  startDate: Date,
  endDate: Date,
  organizationId: string | undefined,
  selectedCategories: string[] = [],
  selectedCertifications: string[] = []
) {
  return useQuery({
    queryKey: ['opportunity-funnel', startDate.toISOString(), endDate.toISOString(), organizationId, selectedCategories, selectedCertifications],
    queryFn: async (): Promise<OpportunityFunnel> => {
      if (!organizationId) {
        return {
          jobsWithDiverseInvited: 0,
          jobsWithDiverseBid: 0,
          jobsAwardedToDiverse: 0,
          totalJobs: 0,
          invitedPercentage: 0,
          bidPercentage: 0,
          awardedPercentage: 0,
        };
      }

      // Fetch jobs in period
      let jobQuery = supabase
        .from('jobs')
        .select(`
          id,
          category,
          status,
          purchase_order:purchase_orders!left(vendor_organization_id)
        `)
        .eq('organization_id', organizationId)
        .gte('updated_at', startDate.toISOString())
        .lte('updated_at', endDate.toISOString());

      if (selectedCategories.length > 0) {
        jobQuery = jobQuery.in('category', selectedCategories);
      }

      const { data: jobs } = await jobQuery;
      const totalJobs = jobs?.length || 0;

      if (totalJobs === 0) {
        return {
          jobsWithDiverseInvited: 0,
          jobsWithDiverseBid: 0,
          jobsAwardedToDiverse: 0,
          totalJobs: 0,
          invitedPercentage: 0,
          bidPercentage: 0,
          awardedPercentage: 0,
        };
      }

      const jobIds = (jobs || []).map(j => j.id);

      // Get RFQs for these jobs
      const { data: rfqs } = await supabase
        .from('rfqs')
        .select('id, job_id')
        .in('job_id', jobIds);

      const rfqIds = (rfqs || []).map(r => r.id);
      const rfqToJobMap = new Map<string, string>();
      for (const rfq of rfqs || []) {
        rfqToJobMap.set(rfq.id, rfq.job_id);
      }

      // Get bids for these RFQs
      const { data: bids } = await supabase
        .from('bids')
        .select('id, rfq_id, vendor_user_id')
        .in('rfq_id', rfqIds);

      // Get vendor profiles
      const vendorUserIds = [...new Set((bids || []).map(b => b.vendor_user_id))];
      
      const { data: vendorProfiles } = await supabase
        .from('vendor_profiles_enhanced')
        .select('id, user_id, organization_id')
        .in('user_id', vendorUserIds);

      const userToProfileMap = new Map<string, string>();
      const userToOrgMap = new Map<string, string>();
      for (const vp of vendorProfiles || []) {
        userToProfileMap.set(vp.user_id, vp.id);
        userToOrgMap.set(vp.user_id, vp.organization_id);
      }

      // Get diverse vendor profile IDs
      const profileIds = (vendorProfiles || []).map(v => v.id);
      const diverseUserIds = new Set<string>();
      const diverseOrgIds = new Set<string>();

      if (profileIds.length > 0) {
        const { data: certs } = await supabase
          .from('vendor_certifications')
          .select(`
            vendor_profile_id,
            certification_type_id,
            certification_type:certification_types!inner(
              category:certification_categories!inner(name)
            )
          `)
          .in('vendor_profile_id', profileIds);

        const diverseProfileIds = new Set<string>();
        for (const cert of certs || []) {
          const catName = (cert.certification_type as any)?.category?.name;
          if (!DIVERSITY_CATEGORY_NAMES.includes(catName)) continue;
          
          if (selectedCertifications.length > 0 && !selectedCertifications.includes(cert.certification_type_id)) {
            continue;
          }
          
          diverseProfileIds.add(cert.vendor_profile_id);
        }

        for (const vp of vendorProfiles || []) {
          if (diverseProfileIds.has(vp.id)) {
            diverseUserIds.add(vp.user_id);
            diverseOrgIds.add(vp.organization_id);
          }
        }
      }

      // Calculate funnel metrics
      const jobsWithDiverseBidSet = new Set<string>();
      for (const bid of bids || []) {
        if (diverseUserIds.has(bid.vendor_user_id)) {
          const jobId = rfqToJobMap.get(bid.rfq_id);
          if (jobId) jobsWithDiverseBidSet.add(jobId);
        }
      }

      // Jobs awarded to diverse vendors
      const jobsAwardedToDiverseSet = new Set<string>();
      for (const job of jobs || []) {
        const po = Array.isArray(job.purchase_order) ? job.purchase_order[0] : job.purchase_order;
        if (po?.vendor_organization_id && diverseOrgIds.has(po.vendor_organization_id)) {
          if (['accepted', 'closed', 'in_progress', 'completed_pending_review', 'awarded'].includes(job.status)) {
            jobsAwardedToDiverseSet.add(job.id);
          }
        }
      }

      // For now, we'll use jobs with bids as a proxy for "invited"
      // since we don't have explicit invitation tracking in our schema
      const jobsWithDiverseInvited = jobsWithDiverseBidSet.size;
      const jobsWithDiverseBid = jobsWithDiverseBidSet.size;
      const jobsAwardedToDiverse = jobsAwardedToDiverseSet.size;

      return {
        jobsWithDiverseInvited,
        jobsWithDiverseBid,
        jobsAwardedToDiverse,
        totalJobs,
        invitedPercentage: totalJobs > 0 ? (jobsWithDiverseInvited / totalJobs) * 100 : 0,
        bidPercentage: jobsWithDiverseInvited > 0 ? (jobsWithDiverseBid / jobsWithDiverseInvited) * 100 : 0,
        awardedPercentage: jobsWithDiverseBid > 0 ? (jobsAwardedToDiverse / jobsWithDiverseBid) * 100 : 0,
      };
    },
    enabled: !!organizationId,
  });
}

// ===========================================
// Hook: useDiversityByCategory
// Spend breakdown by job category
// ===========================================

export function useDiversityByCategory(
  startDate: Date,
  endDate: Date,
  organizationId: string | undefined,
  selectedCategories: string[] = [],
  selectedCertifications: string[] = []
) {
  return useQuery({
    queryKey: ['diversity-by-category', startDate.toISOString(), endDate.toISOString(), organizationId, selectedCategories, selectedCertifications],
    queryFn: async (): Promise<CategoryDiversitySpend[]> => {
      if (!organizationId) return [];

      // Fetch jobs with POs
      let query = supabase
        .from('jobs')
        .select(`
          id,
          category,
          purchase_order:purchase_orders!left(
            po_amount_cents,
            vendor_organization_id
          )
        `)
        .eq('organization_id', organizationId)
        .gte('updated_at', startDate.toISOString())
        .lte('updated_at', endDate.toISOString())
        .in('status', ['accepted', 'closed', 'in_progress', 'completed_pending_review', 'awarded']);

      if (selectedCategories.length > 0) {
        query = query.in('category', selectedCategories);
      }

      const { data: jobs, error } = await query;
      if (error) throw error;

      // Get all vendor org IDs
      const vendorOrgIds = new Set<string>();
      for (const job of jobs || []) {
        const po = Array.isArray(job.purchase_order) ? job.purchase_order[0] : job.purchase_order;
        if (po?.vendor_organization_id) {
          vendorOrgIds.add(po.vendor_organization_id);
        }
      }

      // Fetch diverse vendors
      const diverseVendorOrgIds = await fetchDiverseVendorOrgIds(Array.from(vendorOrgIds), selectedCertifications);

      // Aggregate by category
      const categoryMap = new Map<string, {
        diverseSpendCents: number;
        nonDiverseSpendCents: number;
        diverseJobCount: number;
        totalJobCount: number;
      }>();

      for (const job of jobs || []) {
        const po = Array.isArray(job.purchase_order) ? job.purchase_order[0] : job.purchase_order;
        if (!po?.po_amount_cents) continue;

        const category = job.category || 'other';
        const existing = categoryMap.get(category) || {
          diverseSpendCents: 0,
          nonDiverseSpendCents: 0,
          diverseJobCount: 0,
          totalJobCount: 0,
        };

        existing.totalJobCount++;

        if (po.vendor_organization_id && diverseVendorOrgIds.has(po.vendor_organization_id)) {
          existing.diverseSpendCents += po.po_amount_cents;
          existing.diverseJobCount++;
        } else {
          existing.nonDiverseSpendCents += po.po_amount_cents;
        }

        categoryMap.set(category, existing);
      }

      // Convert to array
      return Array.from(categoryMap.entries())
        .map(([category, data]) => ({
          category: category as JobCategory,
          categoryLabel: getCategoryLabel(category),
          diverseSpendCents: data.diverseSpendCents,
          nonDiverseSpendCents: data.nonDiverseSpendCents,
          totalSpendCents: data.diverseSpendCents + data.nonDiverseSpendCents,
          diversePercentage: (data.diverseSpendCents + data.nonDiverseSpendCents) > 0
            ? (data.diverseSpendCents / (data.diverseSpendCents + data.nonDiverseSpendCents)) * 100
            : 0,
          diverseJobCount: data.diverseJobCount,
          totalJobCount: data.totalJobCount,
        }))
        .sort((a, b) => b.diversePercentage - a.diversePercentage);
    },
    enabled: !!organizationId,
  });
}

// ===========================================
// Hook: useDiversityByGeography
// Spend breakdown by vendor state
// ===========================================

export function useDiversityByGeography(
  startDate: Date,
  endDate: Date,
  organizationId: string | undefined,
  selectedCategories: string[] = [],
  selectedCertifications: string[] = []
) {
  return useQuery({
    queryKey: ['diversity-by-geography', startDate.toISOString(), endDate.toISOString(), organizationId, selectedCategories, selectedCertifications],
    queryFn: async (): Promise<GeographySpend[]> => {
      if (!organizationId) return [];

      // Fetch jobs with POs
      let query = supabase
        .from('jobs')
        .select(`
          id,
          category,
          purchase_order:purchase_orders!left(
            po_amount_cents,
            vendor_organization_id
          )
        `)
        .eq('organization_id', organizationId)
        .gte('updated_at', startDate.toISOString())
        .lte('updated_at', endDate.toISOString())
        .in('status', ['accepted', 'closed', 'in_progress', 'completed_pending_review', 'awarded']);

      if (selectedCategories.length > 0) {
        query = query.in('category', selectedCategories);
      }

      const { data: jobs, error } = await query;
      if (error) throw error;

      // Build vendor -> jobs/spend map
      const vendorJobsMap = new Map<string, { spendCents: number; jobIds: Set<string> }>();

      for (const job of jobs || []) {
        const po = Array.isArray(job.purchase_order) ? job.purchase_order[0] : job.purchase_order;
        if (!po?.vendor_organization_id || !po?.po_amount_cents) continue;

        const existing = vendorJobsMap.get(po.vendor_organization_id) || { spendCents: 0, jobIds: new Set() };
        existing.spendCents += po.po_amount_cents;
        existing.jobIds.add(job.id);
        vendorJobsMap.set(po.vendor_organization_id, existing);
      }

      const vendorOrgIds = Array.from(vendorJobsMap.keys());
      if (vendorOrgIds.length === 0) return [];

      // Fetch vendor profiles with state
      const { data: vendorProfiles } = await supabase
        .from('vendor_profiles_enhanced')
        .select('id, organization_id, state')
        .in('organization_id', vendorOrgIds);

      const orgToStateMap = new Map<string, string>();
      const profileToOrgMap = new Map<string, string>();
      for (const vp of vendorProfiles || []) {
        orgToStateMap.set(vp.organization_id, vp.state || 'Unknown');
        profileToOrgMap.set(vp.id, vp.organization_id);
      }

      // Fetch diverse vendor org IDs
      const diverseVendorOrgIds = await fetchDiverseVendorOrgIds(vendorOrgIds, selectedCertifications);

      // Aggregate by state (only for diverse vendors)
      const stateMap = new Map<string, {
        spendCents: number;
        vendorIds: Set<string>;
        jobIds: Set<string>;
      }>();

      let totalDiverseSpend = 0;

      for (const [orgId, data] of vendorJobsMap.entries()) {
        if (!diverseVendorOrgIds.has(orgId)) continue;

        const state = orgToStateMap.get(orgId) || 'Unknown';
        totalDiverseSpend += data.spendCents;

        const existing = stateMap.get(state) || {
          spendCents: 0,
          vendorIds: new Set(),
          jobIds: new Set(),
        };

        existing.spendCents += data.spendCents;
        existing.vendorIds.add(orgId);
        data.jobIds.forEach(jid => existing.jobIds.add(jid));
        stateMap.set(state, existing);
      }

      // Convert to array and get top 15
      const result: GeographySpend[] = Array.from(stateMap.entries())
        .map(([state, data]) => ({
          state,
          diverseSpendCents: data.spendCents,
          diverseVendorCount: data.vendorIds.size,
          jobCount: data.jobIds.size,
          percentageOfDiverseSpend: totalDiverseSpend > 0 ? (data.spendCents / totalDiverseSpend) * 100 : 0,
        }))
        .sort((a, b) => b.diverseSpendCents - a.diverseSpendCents);

      // Return top 15 + aggregate "Other" if needed
      if (result.length > 15) {
        const top15 = result.slice(0, 15);
        const others = result.slice(15);
        const otherSpend = others.reduce((sum, s) => sum + s.diverseSpendCents, 0);
        const otherVendors = others.reduce((sum, s) => sum + s.diverseVendorCount, 0);
        const otherJobs = others.reduce((sum, s) => sum + s.jobCount, 0);

        return [
          ...top15,
          {
            state: 'Other',
            diverseSpendCents: otherSpend,
            diverseVendorCount: otherVendors,
            jobCount: otherJobs,
            percentageOfDiverseSpend: totalDiverseSpend > 0 ? (otherSpend / totalDiverseSpend) * 100 : 0,
          },
        ];
      }

      return result;
    },
    enabled: !!organizationId,
  });
}

// ===========================================
// Hook: useTopDiverseVendors
// Top diverse vendors table data
// ===========================================

export function useTopDiverseVendors(
  startDate: Date,
  endDate: Date,
  organizationId: string | undefined,
  selectedCategories: string[] = [],
  selectedCertifications: string[] = []
) {
  return useQuery({
    queryKey: ['top-diverse-vendors', startDate.toISOString(), endDate.toISOString(), organizationId, selectedCategories, selectedCertifications],
    queryFn: async (): Promise<TopDiverseVendor[]> => {
      if (!organizationId) return [];

      // Fetch jobs with POs
      let query = supabase
        .from('jobs')
        .select(`
          id,
          category,
          purchase_order:purchase_orders!left(
            po_amount_cents,
            vendor_organization_id
          )
        `)
        .eq('organization_id', organizationId)
        .gte('updated_at', startDate.toISOString())
        .lte('updated_at', endDate.toISOString())
        .in('status', ['accepted', 'closed', 'in_progress', 'completed_pending_review', 'awarded']);

      if (selectedCategories.length > 0) {
        query = query.in('category', selectedCategories);
      }

      const { data: jobs, error } = await query;
      if (error) throw error;

      // Build vendor -> jobs/spend map
      const vendorJobsMap = new Map<string, { spendCents: number; jobCount: number }>();

      for (const job of jobs || []) {
        const po = Array.isArray(job.purchase_order) ? job.purchase_order[0] : job.purchase_order;
        if (!po?.vendor_organization_id || !po?.po_amount_cents) continue;

        const existing = vendorJobsMap.get(po.vendor_organization_id) || { spendCents: 0, jobCount: 0 };
        existing.spendCents += po.po_amount_cents;
        existing.jobCount++;
        vendorJobsMap.set(po.vendor_organization_id, existing);
      }

      const vendorOrgIds = Array.from(vendorJobsMap.keys());
      if (vendorOrgIds.length === 0) return [];

      // Fetch vendor profiles
      const { data: vendorProfiles } = await supabase
        .from('vendor_profiles_enhanced')
        .select('id, organization_id, user_id')
        .in('organization_id', vendorOrgIds);

      const profileToOrgMap = new Map<string, string>();
      const orgToProfileMap = new Map<string, string>();
      const orgToUserMap = new Map<string, string>();
      for (const vp of vendorProfiles || []) {
        profileToOrgMap.set(vp.id, vp.organization_id);
        orgToProfileMap.set(vp.organization_id, vp.id);
        orgToUserMap.set(vp.organization_id, vp.user_id);
      }

      const profileIds = (vendorProfiles || []).map(v => v.id);

      // Fetch certifications
      const { data: certs } = await supabase
        .from('vendor_certifications')
        .select(`
          vendor_profile_id,
          certification_type_id,
          certification_type:certification_types!inner(
            id,
            name,
            category:certification_categories!inner(name)
          )
        `)
        .in('vendor_profile_id', profileIds);

      // Build org -> certifications map (only diversity certs)
      const orgCertsMap = new Map<string, string[]>();
      const diverseOrgIds = new Set<string>();

      for (const cert of certs || []) {
        const catName = (cert.certification_type as any)?.category?.name;
        if (!DIVERSITY_CATEGORY_NAMES.includes(catName)) continue;
        
        if (selectedCertifications.length > 0 && !selectedCertifications.includes(cert.certification_type_id)) {
          continue;
        }

        const orgId = profileToOrgMap.get(cert.vendor_profile_id);
        if (!orgId) continue;

        diverseOrgIds.add(orgId);
        const existing = orgCertsMap.get(orgId) || [];
        const certName = (cert.certification_type as any)?.name || 'Unknown';
        if (!existing.includes(certName)) {
          existing.push(certName);
        }
        orgCertsMap.set(orgId, existing);
      }

      // Fetch vendor names
      const { data: orgs } = await supabase
        .from('organizations')
        .select('id, name')
        .in('id', Array.from(diverseOrgIds));

      const orgNameMap = new Map<string, string>();
      for (const org of orgs || []) {
        orgNameMap.set(org.id, org.name);
      }

      // Calculate total diverse spend for percentage
      let totalDiverseSpend = 0;
      for (const orgId of diverseOrgIds) {
        const data = vendorJobsMap.get(orgId);
        if (data) totalDiverseSpend += data.spendCents;
      }

      // Get bid counts for win rate calculation
      const jobIds = (jobs || []).map(j => j.id);
      const { data: rfqs } = await supabase
        .from('rfqs')
        .select('id, job_id')
        .in('job_id', jobIds);

      const rfqIds = (rfqs || []).map(r => r.id);
      const { data: bids } = await supabase
        .from('bids')
        .select('id, rfq_id, vendor_user_id')
        .in('rfq_id', rfqIds);

      // Count bids per vendor org
      const orgBidsMap = new Map<string, number>();
      for (const bid of bids || []) {
        for (const vp of vendorProfiles || []) {
          if (vp.user_id === bid.vendor_user_id) {
            const count = orgBidsMap.get(vp.organization_id) || 0;
            orgBidsMap.set(vp.organization_id, count + 1);
            break;
          }
        }
      }

      // Build result
      const result: TopDiverseVendor[] = Array.from(diverseOrgIds)
        .map(orgId => {
          const data = vendorJobsMap.get(orgId)!;
          const bidsSubmitted = orgBidsMap.get(orgId) || 0;
          
          return {
            vendorId: orgId,
            vendorName: orgNameMap.get(orgId) || 'Unknown Vendor',
            certifications: orgCertsMap.get(orgId) || [],
            jobsAwarded: data.jobCount,
            totalSpendCents: data.spendCents,
            percentageOfDiverseSpend: totalDiverseSpend > 0 ? (data.spendCents / totalDiverseSpend) * 100 : 0,
            bidsSubmitted,
            winRate: bidsSubmitted > 0 ? (data.jobCount / bidsSubmitted) * 100 : 0,
            avgJobValueCents: data.jobCount > 0 ? Math.round(data.spendCents / data.jobCount) : 0,
          };
        })
        .sort((a, b) => b.totalSpendCents - a.totalSpendCents);

      return result;
    },
    enabled: !!organizationId,
  });
}

// ===========================================
// Hook: useDiversityExportData
// Comprehensive export data
// ===========================================

export function useDiversityExportData(
  startDate: Date,
  endDate: Date,
  organizationId: string | undefined,
  selectedCategories: string[] = [],
  selectedCertifications: string[] = []
) {
  return useQuery({
    queryKey: ['diversity-export', startDate.toISOString(), endDate.toISOString(), organizationId, selectedCategories, selectedCertifications],
    queryFn: async (): Promise<DiversityExportData['jobs']> => {
      if (!organizationId) return [];

      // Fetch jobs with POs
      let query = supabase
        .from('jobs')
        .select(`
          id,
          public_id,
          title,
          category,
          updated_at,
          purchase_order:purchase_orders!left(
            po_amount_cents,
            vendor_organization_id
          )
        `)
        .eq('organization_id', organizationId)
        .gte('updated_at', startDate.toISOString())
        .lte('updated_at', endDate.toISOString())
        .in('status', ['accepted', 'closed', 'in_progress', 'completed_pending_review', 'awarded']);

      if (selectedCategories.length > 0) {
        query = query.in('category', selectedCategories);
      }

      const { data: jobs, error } = await query;
      if (error) throw error;

      // Collect vendor org IDs
      const vendorOrgIds = new Set<string>();
      for (const job of jobs || []) {
        const po = Array.isArray(job.purchase_order) ? job.purchase_order[0] : job.purchase_order;
        if (po?.vendor_organization_id) {
          vendorOrgIds.add(po.vendor_organization_id);
        }
      }

      // Fetch vendor names
      const { data: orgs } = await supabase
        .from('organizations')
        .select('id, name')
        .in('id', Array.from(vendorOrgIds));

      const orgNameMap = new Map<string, string>();
      for (const org of orgs || []) {
        orgNameMap.set(org.id, org.name);
      }

      // Fetch vendor profiles
      const { data: vendorProfiles } = await supabase
        .from('vendor_profiles_enhanced')
        .select('id, organization_id')
        .in('organization_id', Array.from(vendorOrgIds));

      const profileToOrgMap = new Map<string, string>();
      for (const vp of vendorProfiles || []) {
        profileToOrgMap.set(vp.id, vp.organization_id);
      }

      const profileIds = (vendorProfiles || []).map(v => v.id);

      // Fetch certifications
      const { data: certs } = await supabase
        .from('vendor_certifications')
        .select(`
          vendor_profile_id,
          certification_type:certification_types!inner(
            name,
            category:certification_categories!inner(name)
          )
        `)
        .in('vendor_profile_id', profileIds);

      // Build org -> certifications map
      const orgCertsMap = new Map<string, string[]>();
      for (const cert of certs || []) {
        const catName = (cert.certification_type as any)?.category?.name;
        if (!DIVERSITY_CATEGORY_NAMES.includes(catName)) continue;

        const orgId = profileToOrgMap.get(cert.vendor_profile_id);
        if (!orgId) continue;

        const existing = orgCertsMap.get(orgId) || [];
        const certName = (cert.certification_type as any)?.name || 'Unknown';
        if (!existing.includes(certName)) {
          existing.push(certName);
        }
        orgCertsMap.set(orgId, existing);
      }

      // Build export data - only include jobs with diverse vendors
      return (jobs || [])
        .filter(job => {
          const po = Array.isArray(job.purchase_order) ? job.purchase_order[0] : job.purchase_order;
          return po?.vendor_organization_id && orgCertsMap.has(po.vendor_organization_id);
        })
        .map(job => {
          const po = Array.isArray(job.purchase_order) ? job.purchase_order[0] : job.purchase_order;
          const vendorOrgId = po?.vendor_organization_id || '';
          
          return {
            jobId: job.id,
            publicId: job.public_id,
            title: job.title,
            category: getCategoryLabel(job.category || 'other'),
            vendorName: orgNameMap.get(vendorOrgId) || 'Unknown',
            certifications: orgCertsMap.get(vendorOrgId) || [],
            amountCents: po?.po_amount_cents || 0,
            awardedDate: format(new Date(job.updated_at), 'yyyy-MM-dd'),
          };
        });
    },
    enabled: !!organizationId,
  });
}
